import type { Fixture, Team, TeamHistorySample, Bookmaker, UserProfile } from "@tracker-bet-analystic/domain";

export const DEMO_TEAMS: Team[] = [
  { id: "team-1", provider: "demo", providerId: "1", name: "São Paulo FC", shortName: "SPFC", country: "Brasil" },
  { id: "team-2", provider: "demo", providerId: "2", name: "Palmeiras", shortName: "PAL", country: "Brasil" },
  { id: "team-3", provider: "demo", providerId: "3", name: "Flamengo", shortName: "FLA", country: "Brasil" },
  { id: "team-4", provider: "demo", providerId: "4", name: "Corinthians", shortName: "COR", country: "Brasil" },
  { id: "team-5", provider: "demo", providerId: "5", name: "Santos FC", shortName: "SAN", country: "Brasil" },
  { id: "team-6", provider: "demo", providerId: "6", name: "Grêmio", shortName: "GRE", country: "Brasil" },
  { id: "team-7", provider: "demo", providerId: "7", name: "Internacional", shortName: "INT", country: "Brasil" },
  { id: "team-8", provider: "demo", providerId: "8", name: "Atlético-MG", shortName: "CAM", country: "Brasil" },
  { id: "team-9", provider: "demo", providerId: "9", name: "Fluminense", shortName: "FLU", country: "Brasil" },
  { id: "team-10", provider: "demo", providerId: "10", name: "Botafogo", shortName: "BOT", country: "Brasil" },
  { id: "team-11", provider: "demo", providerId: "11", name: "Bahia", shortName: "BAH", country: "Brasil" },
  { id: "team-12", provider: "demo", providerId: "12", name: "Fortaleza", shortName: "FOR", country: "Brasil" },
  { id: "team-13", provider: "demo", providerId: "13", name: "Ceará", shortName: "CEA", country: "Brasil" },
  { id: "team-14", provider: "demo", providerId: "14", name: "Vasco da Gama", shortName: "VAS", country: "Brasil" },
  { id: "team-15", provider: "demo", providerId: "15", name: "Cruzeiro", shortName: "CRU", country: "Brasil" },
  { id: "team-16", provider: "demo", providerId: "16", name: "Athletico-PR", shortName: "CAP", country: "Brasil" },
  { id: "team-17", provider: "demo", providerId: "17", name: "Bragantino", shortName: "RBB", country: "Brasil" },
  { id: "team-18", provider: "demo", providerId: "18", name: "Juventude", shortName: "JUV", country: "Brasil" },
  { id: "team-19", provider: "demo", providerId: "19", name: "Criciúma", shortName: "CRI", country: "Brasil" },
  { id: "team-20", provider: "demo", providerId: "20", name: "Vitória", shortName: "VIT", country: "Brasil" },
];

const DEMO_TEAM_WITHOUT_HISTORY = "team-20";

function hashSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) || 1;
}

function createSeededRandom(seed: number): () => number {
  let state = seed % 2147483647;
  if (state <= 0) state += 2147483646;
  return () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
}

export function createDemoFixture(
  id: string,
  homeTeamId: string,
  awayTeamId: string,
  competitionName: string,
  daysFromNow: number,
  hour: number = 20
): Fixture {
  const kickoff = new Date();
  kickoff.setDate(kickoff.getDate() + daysFromNow);
  kickoff.setHours(hour, 0, 0, 0);
  const homeTeam = DEMO_TEAMS.find((t) => t.id === homeTeamId);
  const awayTeam = DEMO_TEAMS.find((t) => t.id === awayTeamId);
  return {
    id,
    provider: "demo",
    providerId: `demo-${id}`,
    competitionId: "comp-1",
    competitionName,
    season: String(new Date().getFullYear()),
    homeTeamId,
    homeTeamName: homeTeam?.name ?? homeTeamId,
    awayTeamId,
    awayTeamName: awayTeam?.name ?? awayTeamId,
    kickoffAt: kickoff.toISOString(),
    status: "scheduled",
    venue: "Estádio Demo",
    referee: "Árbitro Demo",
  };
}

export const DEMO_FIXTURES: Fixture[] = [
  createDemoFixture("fix-1", "team-1", "team-2", "Brasileirão Série A", 1),
  createDemoFixture("fix-2", "team-3", "team-4", "Brasileirão Série A", 2, 16),
  createDemoFixture("fix-3", "team-5", "team-6", "Brasileirão Série A", 3, 21),
  createDemoFixture("fix-4", "team-7", "team-8", "Copa do Brasil", 4, 19),
  createDemoFixture("fix-5", "team-9", "team-10", "Libertadores", 5, 21),
  createDemoFixture("fix-6", "team-2", "team-1", "Brasileirão Série A", 6),
  createDemoFixture("fix-7", "team-4", "team-3", "Brasileirão Série A", 7, 16),
  createDemoFixture("fix-8", "team-6", "team-5", "Brasileirão Série A", 8, 19),
  createDemoFixture("fix-9", "team-11", "team-12", "Brasileirão Série A", 1, 19),
  createDemoFixture("fix-10", "team-13", "team-14", "Brasileirão Série A", 2, 21),
  createDemoFixture("fix-11", "team-15", "team-16", "Copa do Brasil", 3, 20),
  createDemoFixture("fix-12", "team-17", "team-18", "Brasileirão Série A", 4),
  createDemoFixture("fix-13", "team-19", "team-20", "Brasileirão Série A", 5, 16),
  createDemoFixture("fix-14", "team-8", "team-7", "Libertadores", 6, 19),
  createDemoFixture("fix-15", "team-10", "team-9", "Brasileirão Série A", 7, 21),
  createDemoFixture("fix-16", "team-12", "team-11", "Copa do Brasil", 8, 20),
];

function createHistorySamples(teamId: string, count: number): TeamHistorySample[] {
  if (teamId === DEMO_TEAM_WITHOUT_HISTORY) return [];

  const random = createSeededRandom(hashSeed(teamId));
  const baseGoalsFor = 1.1 + random() * 1.0;
  const baseGoalsAgainst = 1.0 + random() * 0.7;
  const baseShots = 10 + random() * 5;
  const baseCorners = 4 + random() * 4;
  const basePossession = 46 + random() * 10;

  return Array.from({ length: count }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (i + 1) * 7 - Math.floor(random() * 3));

    const isHome = i % 2 === 0;
    const venueBias = isHome ? 0.3 : -0.2;
    const concessionBias = isHome ? -0.2 : 0.3;

    const goalsFor = Math.max(0, Math.round(baseGoalsFor + venueBias + (random() - 0.5) * 1.6));
    const goalsAgainst = Math.max(0, Math.round(baseGoalsAgainst + concessionBias + (random() - 0.5) * 1.4));

    const missingAdvanced = i % 7 === 3;
    const missingDistribution = i % 5 === 4;

    return {
      fixtureId: `${teamId}-hist-${i}`,
      date: date.toISOString(),
      isHome,
      goalsFor,
      goalsAgainst,
      shots: Math.max(0, Math.round(baseShots + (isHome ? 2 : -1) + (random() - 0.5) * 5)),
      shotsOnTarget: Math.max(0, Math.round(4 + (isHome ? 1 : -0.5) + (random() - 0.5) * 2.5)),
      corners: Math.max(0, Math.round(baseCorners + (isHome ? 1 : -1) + (random() - 0.5) * 3)),
      yellowCards: Math.max(0, Math.round(1.8 + (random() - 0.5) * 1.5)),
      redCards: random() > 0.92 ? 1 : 0,
      possession: missingDistribution ? null : Math.round(basePossession + (isHome ? 4 : -4) + (random() - 0.5) * 10),
      passes: missingDistribution ? null : Math.round(400 + (isHome ? 30 : -30) + (random() - 0.5) * 90),
      xGFor: missingAdvanced ? null : Number((goalsFor * 0.85 + random() * 0.6).toFixed(2)),
      xGAgainst: missingAdvanced ? null : Number((goalsAgainst * 0.85 + random() * 0.6).toFixed(2)),
    };
  });
}

export const DEMO_HISTORY_SAMPLES: Record<string, TeamHistorySample[]> = {};

DEMO_TEAMS.forEach((team) => {
  DEMO_HISTORY_SAMPLES[team.id] = createHistorySamples(team.id, 20);
});

export const COMPETITIONS = [
  { id: "comp-1", provider: "demo", providerId: "1", name: "Brasileirão Série A", country: "Brasil", tier: 1 },
  { id: "comp-2", provider: "demo", providerId: "2", name: "Copa do Brasil", country: "Brasil", tier: 1 },
  { id: "comp-3", provider: "demo", providerId: "3", name: "Libertadores", country: "América do Sul", tier: 1 },
  { id: "comp-4", provider: "demo", providerId: "4", name: "Sul-Americana", country: "América do Sul", tier: 2 },
];

export const DEMO_BOOKMAKERS: Bookmaker[] = [
  {
    id: "bm-1",
    userId: "demo-user-001",
    name: "Casa Alpha",
    slot: 1,
    isActive: true,
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "bm-2",
    userId: "demo-user-001",
    name: "Casa Beta",
    slot: 2,
    isActive: true,
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "bm-3",
    userId: "demo-user-001",
    name: "Casa Gamma",
    slot: 3,
    isActive: false,
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
];

export const DEMO_PROFILE: UserProfile = {
  userId: "demo-user-001",
  timezone: "America/Sao_Paulo",
  language: "pt-BR",
  telegramLinked: false,
  createdAt: "2024-01-01T00:00:00Z",
  updatedAt: "2024-01-01T00:00:00Z",
};